/**
 * Registry for experiment variants
 *
 * This module allows AbTest components to register their available variants
 * so that DevDash can display them automatically without hardcoding.
 *
 * Usage:
 * - AbTest calls registerExperimentVariants() when it mounts
 * - DevDash calls getExperimentVariants() to get available variants for display
 */

/** Default variants for simple on/off tests */
const DEFAULT_VARIANTS = ['on', 'off'];

/** Map of experiment names to their available variants */
const experimentVariantsRegistry = new Map<string, string[]>();

/** Set of listeners to notify when registry changes */
const listeners = new Set<() => void>();

/**
 * Register available variants for an experiment
 * Called by AbTest components when they mount
 */
export function registerExperimentVariants(flagName: string, variants: string[]): void {
  const existing = experimentVariantsRegistry.get(flagName);

  // Merge variants if already registered (multiple AbTest instances for same flag)
  if (existing) {
    const merged = [...new Set([...existing, ...variants])];
    experimentVariantsRegistry.set(flagName, merged);
  } else {
    experimentVariantsRegistry.set(flagName, variants);
  }

  // Notify listeners
  listeners.forEach((listener) => listener());
}

/**
 * Get available variants for an experiment
 * Returns undefined if not registered (DevDash will fall back to defaults)
 */
export function getExperimentVariants(flagName: string): string[] | undefined {
  return experimentVariantsRegistry.get(flagName);
}

/**
 * Get all registered experiments and their variants
 */
export function getAllExperimentVariants(): Map<string, string[]> {
  return new Map(experimentVariantsRegistry);
}

/**
 * Subscribe to registry changes
 * Returns unsubscribe function
 */
export function subscribeToRegistry(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Get default variants for simple on/off tests
 */
export function getDefaultVariants(): string[] {
  return DEFAULT_VARIANTS;
}
